/**
 * 청구 일정 알림 수정 컴포넌트
 * 계약의 청구 일정 알림 정보를 수정하는 컴포넌트입니다.
 * 공문과 알림톡 종류를 설정하고 각각의 발송 여부를 관리할 수 있습니다.
 */

import { useMemo } from "react";
import { Control, Controller } from "react-hook-form";
import { ScheduleNoti } from "src/api/billingSchedule/billing-type";
import { ContractorNoti, DetailTotalData } from "src/api/contract/contract-types";
import { BaseCheckbox, BaseInput, BaseTooltip } from "src/components";
import BaseSection from "src/components/layout/BaseSection";
import { BULLET, formatPhoneNumber } from "src/utils";

// 안내 메시지 툴팁
const NOTIFICATION_TOOLTIP = {
  // CONTRACT_ACCEPT_NOTICE: "계약 상태가 '계약체결'로 변경되는 즉시 발송됩니다.", // 계약체결 안내 메시지
  // 계약체결 안내 메시지
  CONTRACT_ACCEPT_NOTICE: <span>계약 상태가 '계약체결'로 변경되는 즉시 발송됩니다.</span>,

  // 결제일 안내 메시지
  CHARGE_NOTICE: (
    <>
      <span>청구일 오전 9시부터 순차적으로 청구 안내가 발송됩니다.</span>
    </>
  ),

  // 미납 안내 메시지
  UNPAID_NOTICE: (
    <>
      <span>납부기한 다음날부터 순차 발송됩니다.</span>
      <br />
      <ul className="ml20">
        <li>{`이메일 : 오전 11시부터`}</li>
        <li>{`알림톡 : 오후 1시부터`}</li>
      </ul>
    </>
  ),

  // 계약만료 안내 메시지
  EXPIRE_CONTRACTOR_NOTICE: (
    <>
      <span>계약자(임차인)에게 만료 안내가 발송됩니다.</span>
      <br />
      <ul className="ml20">
        <li>{`이메일(1회 발송) : 계약 종료 90일 전, 오전 11시부터`}</li>
        <li>{`알림톡(3회 발송) : 계약 종료 120/90/60일 전, 오전 11시부터`}</li>
      </ul>
    </>
  ), // 계약만료 안내 메시지

  // 연체료 안내 메시지
  EXPIRE_PROVIDER_NOTICE: (
    <>
      <span>프로바이더(상품 등록 및 운영 주체)에게 만료 안내가 발송됩니다.</span>
      <br />
      <ul className="ml20">
        <li>{`설정 위치 : 정산정보 > 연락처 정보 > 계약 진행 담당`}</li>
        <li>{`알림톡(3회 발송) : 계약 종료 120/90/60일 전, 오전 11시부터`}</li>
      </ul>
    </>
  ), // 연체료 안내 메시지
} as const;

interface NotificationRow {
  label: string;
  email?: ScheduleNoti;
  kakao?: ScheduleNoti;
}

interface NotificationFormData {
  notificationRows: NotificationRow[];
  billContactList: ContractorNoti[];
}

type Props = {
  editable: boolean;
  control: Control<NotificationFormData>;
  notificationRows: NotificationRow[];
  billContactList: ContractorNoti[];
  detailData: DetailTotalData;
  onNotificationToggle: (messageId: string | number | undefined, checked: boolean) => void;
  onAddEmail: () => void;
  onRemoveEmail: (index: number) => void;
};

const ContractNotificationForm = ({
  editable,
  control,
  notificationRows,
  billContactList,
  detailData,
  onNotificationToggle,
  onAddEmail,
  onRemoveEmail,
}: Props) => {
  const { contract, provider, contractManage } = detailData;

  const notificationDisabled = useMemo(() => {
    const step = contract?.contractStep;
    // 공간임대(FULL_COURT)는 이용완료/해지완료에도 수신자 설정 수정 가능
    if (detailData?.product?.productType === "FULL_COURT" && (step === "USE_COMPLETE" || step === "TERMINATE_COMPLETE")) {
      return false;
    }
    return step === "APPLY_CANCEL" || step === "TERMINATE_COMPLETE" || step === "APPLY_CONFIRM" || step === "USE_COMPLETE";
  }, [contract?.contractStep, detailData?.product?.productType]);

  return (
    <>
      <div className="mb20">
        <h2 className="font-weight-semibold mb20 py10">알림 설정</h2>
        <div className="fles-row flex-center">
          <div className="w-100">
            {notificationRows.map((row, idx) => {
              return (
                <BaseSection
                  key={row.label + idx}
                  className={idx === 4 ? "pt2" : ""}
                  titleChildren={
                    <div className="flex-center">
                      <p className={(idx === 4 && "minmax110") || ""}>{row.label}</p>
                      <BaseTooltip
                        type="pre-formatted"
                        touchIcon="QUESTION"
                        children={<div>{NOTIFICATION_TOOLTIP[row.kakao?.messageType as keyof typeof NOTIFICATION_TOOLTIP]}</div>}
                      />
                    </div>
                  }
                  children={
                    <div className="flex-row flex-center-start ml20">
                      {row.email ? (
                        <BaseCheckbox
                          label="이메일"
                          className="mr10"
                          name={row.label}
                          checked={row.email.isSend || false}
                          onChange={(checked: boolean) => {
                            onNotificationToggle(row.email?.messageId, checked);
                          }}
                          id={`email_${row.email.messageId}`}
                          disabled={!editable}
                        />
                      ) : (
                        <div className="minmax64 mr10"></div>
                      )}

                      {row.kakao && (
                        <BaseCheckbox
                          label="알림톡"
                          name={row.label}
                          checked={row.kakao.isSend || false}
                          onChange={(checked: boolean) => {
                            onNotificationToggle(row.kakao?.messageId, checked);
                          }}
                          id={`kakao_${row.kakao.messageId}`}
                          disabled={!editable}
                          // className={"mb10"}
                        />
                      )}
                    </div>
                  }
                />
              );
            })}
          </div>
        </div>
      </div>
      <div>
        <h2 className="font-weight-semibold mb20 py10">수신자 설정</h2>
        <div>
          <BaseSection
            title={editable ? `이메일 (${billContactList.filter((item) => (item as any).cmdType !== "D").length}/5)` : `이메일`}
            required={editable}
            children={
              <div className="minmax300">
                {billContactList
                  .map((item, idx) => ({ ...item, originalIndex: idx }))
                  .filter((item) => (item as any).cmdType !== "D")
                  .map((item) => (
                    <div key={item.billContactId || item.originalIndex}>
                      {editable && (
                        <div className="flex-center mb10">
                          <Controller
                            name={`billContactList.${item.originalIndex}.email`}
                            control={control}
                            rules={{
                              validate: (value, formValues) => {
                                // cmdType이 "D"면 validation 건너뛰기
                                const currentItem = formValues.billContactList?.[item.originalIndex];

                                if (currentItem?.cmdType === "D") return true;

                                if (!value) return "필수입력 항목입니다.";
                                if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                                  return "올바른 이메일 형식이 아닙니다";
                                }
                                return true;
                              },
                            }}
                            render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                              <>
                                <BaseInput
                                  onChange={onChange}
                                  value={value}
                                  className="minmax240"
                                  name={name}
                                  placeholder="이메일을 입력하세요"
                                  readonly={notificationDisabled}
                                  errorText={error?.message}
                                />
                                <div className="flex-center ml10">
                                  {(() => {
                                    const visibleEmails = billContactList
                                      .map((item, idx) => ({ ...item, originalIndex: idx }))
                                      .filter((item) => (item as any).cmdType !== "D");

                                    const isFirstItem = visibleEmails.findIndex((v) => v.originalIndex === item.originalIndex) === 0;
                                    const canDelete = visibleEmails.length > 1;

                                    return isFirstItem ? (
                                      <>
                                        <button
                                          className={`plus-btn mr10 ${error ? "mb20" : ""}`}
                                          disabled={notificationDisabled}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            onAddEmail();
                                          }}
                                        />
                                        <button
                                          className={`minus-btn  ${error ? "mb20" : ""}`}
                                          disabled={!canDelete || notificationDisabled}
                                          onClick={(e) => {
                                            e.preventDefault();
                                            if (!canDelete) return;
                                            onRemoveEmail(item.originalIndex);
                                          }}
                                        />
                                      </>
                                    ) : (
                                      <button
                                        className={`minus-btn ${error ? "mb20" : ""}`}
                                        disabled={notificationDisabled}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          onRemoveEmail(item.originalIndex);
                                        }}
                                      />
                                    );
                                  })()}
                                </div>
                              </>
                            )}
                          />
                        </div>
                      )}
                      {!editable && (
                        <div className="my10">
                          <span>{item.email}</span>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            }
          />

          <BaseSection
            title={`알림톡`}
            children={
              <div className="minmax240">
                {editable && (
                  <BaseInput
                    disabled
                    value={formatPhoneNumber(contractManage?.contractorList?.find((item) => item.isContractor)?.memberPhoneNumber || "-")}
                  />
                )}
                {!editable && (
                  <div className="my10">
                    <span>{formatPhoneNumber(contractManage?.contractorList?.find((item) => item.isContractor)?.memberPhoneNumber || "-")}</span>
                  </div>
                )}
              </div>
            }
          />
          <BaseSection
            title={`알림톡(프로바이더)`}
            children={
              <div className="minmax240">
                {editable &&
                  provider?.providerContactList
                    ?.filter((item) => item.assignedWorkType === "NORMAL_PROVIDE")
                    .map((item) => <BaseInput key={item.contactId} disabled className="mb10" value={item.contact || "-"} />)}
                {!editable && (
                  <div className="my10">
                    {provider?.providerContactList
                      ?.filter((item) => item.assignedWorkType === "NORMAL_PROVIDE")
                      .map((item) => (
                        <p key={item.contactId} className="mb10">
                          {item.contact || "-"}
                        </p>
                      ))}
                  </div>
                )}
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};
export default ContractNotificationForm;
